import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import profileService from "services/profileService";
import { OLIVEEB_COLORS } from "workspaces/colors";
import partnersService from "services/partnersService";

const { Text, Title } = Typography;

const ORGANIZATION_ID_FIELD = `business${"Entity"}Id`;
const SUBMISSION_TYPES = {
  INDIVIDUAL: "INDIVIDUAL",
  ORGANIZATION: "ORGANIZATION",
};

const option = (value) => ({
  value,
  label: String(value)
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" "),
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const money = (amount, currency = "USD") => {
  const numeric = Number(amount || 0);
  if (!Number.isFinite(numeric)) return `${currency} 0`;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(numeric);
};

const partnerButtonBase = {
  borderRadius: 8,
  fontWeight: 700,
  boxShadow: `0 8px 18px ${OLIVEEB_COLORS.shadow}`,
};

const partnerButtonStyles = {
  primary: {
    ...partnerButtonBase,
    background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebBlue}, ${OLIVEEB_COLORS.oliveebDarkBlue})`,
    borderColor: OLIVEEB_COLORS.oliveebBlue,
    color: OLIVEEB_COLORS.white,
  },
  submit: {
    ...partnerButtonBase,
    background: OLIVEEB_COLORS.white,
    borderColor: OLIVEEB_COLORS.oliveebGreen,
    color: OLIVEEB_COLORS.oliveebGreen,
  },
  refresh: {
    ...partnerButtonBase,
    background: OLIVEEB_COLORS.oliveebGreenSoft,
    borderColor: OLIVEEB_COLORS.oliveebGreen,
    color: OLIVEEB_COLORS.oliveebGreen,
  },
  back: {
    ...partnerButtonBase,
    background: OLIVEEB_COLORS.oliveebBlueSoft,
    borderColor: OLIVEEB_COLORS.oliveebBlue,
    color: OLIVEEB_COLORS.oliveebBlue,
  },
  profile: {
    ...partnerButtonBase,
    background: OLIVEEB_COLORS.greenSoft2,
    borderColor: OLIVEEB_COLORS.oliveebDarkGreen,
    color: OLIVEEB_COLORS.oliveebDarkGreen,
  },
  organization: {
    ...partnerButtonBase,
    background: OLIVEEB_COLORS.oliveebBlueSoft,
    borderColor: OLIVEEB_COLORS.oliveebBlue,
    color: OLIVEEB_COLORS.oliveebBlue,
  },
  cancel: {
    ...partnerButtonBase,
    background: OLIVEEB_COLORS.redSoft,
    borderColor: OLIVEEB_COLORS.oliveebRed,
    color: OLIVEEB_COLORS.oliveebRed,
  },
};

const partnerName = (partner) =>
  partner?.displayName || partner?.email || partner?.referralCode || partner?.id || "Partner";

const partnerOrganizationId = (partner) =>
  partner?.organizationId || partner?.[ORGANIZATION_ID_FIELD] || "";

const organizationName = (organization) =>
  organization?.name ||
  organization?.organizationName ||
  organization?.businessName ||
  organization?.legalName ||
  organization?.displayName ||
  organization?.id ||
  "Organization";

const organizationEmail = (organization) =>
  organization?.contactInformation?.primaryEmail ||
  organization?.contactInformation?.secondaryEmail ||
  organization?.email ||
  organization?.contactEmail ||
  organization?.contactDetails?.email ||
  "";

const organizationPhone = (organization) =>
  organization?.contactInformation?.primaryPhone ||
  organization?.contactInformation?.secondaryPhone ||
  organization?.phone ||
  organization?.contactPhone ||
  organization?.contactDetails?.phone ||
  "";

const organizationAddress = (organization) =>
  organization?.locationInformation ||
  organization?.address ||
  organization?.billingAddress ||
  organization?.organizationLocations?.[0]?.locationInformation ||
  organization?.organizationLocations?.[0] ||
  null;

const profileValue = (profile, ...fields) => {
  for (const field of fields) {
    const value = field.split(".").reduce((source, key) => source?.[key], profile);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const formatAddress = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address;
  return [
    address.street,
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.county,
    address.state,
    address.zipCode || address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const maskedSensitive = (value) => {
  if (!value) return "";
  const text = String(value);
  if (text.length <= 4) return "Provided";
  return `***-${text.slice(-4)}`;
};

const InfoLine = ({ label, value, warning, copyLabel, copyValue, code }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "minmax(130px, 180px) 1fr",
      gap: 10,
      padding: "5px 0",
      borderBottom: `1px solid ${OLIVEEB_COLORS.line}`,
    }}
  >
    <Text type="secondary" style={{ fontSize: 14 }}>{label}</Text>
    <Space size={6} align="start">
      <Text
        code={code}
        strong={!code}
        style={{
          color: warning ? OLIVEEB_COLORS.oliveebOrange : OLIVEEB_COLORS.textPrimary,
          fontSize: 14,
          textAlign: "left",
          wordBreak: "break-word",
          whiteSpace: "normal",
        }}
      >
        {value || "Missing"}
      </Text>
      {copyValue ? (
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyText(copyValue, copyLabel || label)}
          style={partnerButtonStyles.back}
        />
      ) : null}
    </Space>
  </div>
);

const copyText = async (value, label) => {
  if (!value) return;
  await navigator.clipboard.writeText(value);
  message.success(`${label} copied`);
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["ACTIVE", "APPROVED"].includes(normalized)) return "green";
  if (["SUSPENDED", "CANCELLED", "CANCELED", "DECLINED"].includes(normalized)) return "red";
  return "gold";
};

const isActivePartner = (partner) =>
  String(partner?.status || "").toUpperCase() === "ACTIVE";

const isPendingPartner = (partner) =>
  String(partner?.status || "").toUpperCase() === "PENDING";

const isCancelledPartner = (partner) =>
  ["CANCELLED", "CANCELED"].includes(String(partner?.status || "").toUpperCase());

const canCancelPartner = (partner) =>
  isActivePartner(partner) || isPendingPartner(partner);

const needsReactivation = (partner) =>
  ["CANCELLED", "INACTIVE", "SUSPENDED"].includes(
    String(partner?.status || "").toUpperCase(),
  );

const userDisplayName = (user) =>
  [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  user?.name ||
  user?.username ||
  user?.email ||
  "Ovacus Partner";

const userEmail = (user) =>
  user?.email ||
  user?.contactDetails?.personalEmail ||
  user?.contactDetails?.workEmail ||
  user?.personalEmail ||
  "";

const Partners = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [profileDetails, setProfileDetails] = useState(user || null);
  const [organizations, setOrganizations] = useState([]);
  const [partnersPage, setPartnersPage] = useState({ content: [], totalElements: 0 });
  const [partnersPaging, setPartnersPaging] = useState({ page: 0, size: 20 });
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerFormOpen, setPartnerFormOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerSaving, setPartnerSaving] = useState(false);
  const [partnerCancelling, setPartnerCancelling] = useState(false);
  const [partnerForm] = Form.useForm();

  const [commissionsPage, setCommissionsPage] = useState({ content: [], totalElements: 0 });
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const submissionType =
    Form.useWatch("submissionType", partnerForm) || SUBMISSION_TYPES.INDIVIDUAL;
  const selectedOrganizationId = Form.useWatch("organizationId", partnerForm);
  const selectedSubmissionOrganization =
    organizations.find((organization) => organization.id === selectedOrganizationId) || null;

  const findCurrentPartner = useCallback((partners = [], orgs = organizations) => {
    const organizationIds = new Set((orgs || []).map((organization) => organization.id).filter(Boolean));
    return (
      partners.find((item) => item?.partnerType === "USER" && item?.userId === actorId) ||
      partners.find(
        (item) =>
          item?.partnerType === "ORGANIZATION" &&
          organizationIds.has(partnerOrganizationId(item)),
      ) ||
      null
    );
  }, [actorId, organizations]);

  const loadPartners = useCallback(async (page = partnersPaging.page, size = partnersPaging.size) => {
    try {
      setPartnersLoading(true);
      const [organizationsResult, partnersResult] = await Promise.all([
        profileService.organizations.listForUser(actorId, { page: 0, size: 100 }),
        partnersService.list({ page, size: Math.max(size, 100) }),
      ]);
      const organizationContent = Array.isArray(organizationsResult?.content)
        ? organizationsResult.content
        : Array.isArray(organizationsResult)
          ? organizationsResult
          : [];
      const currentPartner = findCurrentPartner(partnersResult.content || [], organizationContent);
      setOrganizations(organizationContent);
      setProfileDetails(user || null);
      setPartnersPage(partnersResult);
      setPartnersPaging({ page, size: Math.max(size, 100) });
      setSelectedPartner(currentPartner);
    } catch (error) {
      message.error(error?.message || "Failed to load partners.");
    } finally {
      setPartnersLoading(false);
    }
  }, [actorId, findCurrentPartner, partnersPaging.page, partnersPaging.size, user]);

  const loadCommissions = useCallback(async (
    partnerId = selectedPartner?.id,
    page = 0,
    size = 100,
  ) => {
    if (!partnerId) {
      setCommissionsPage({ content: [], totalElements: 0 });
      return;
    }

    try {
      setCommissionsLoading(true);
      const result = await partnersService.listCommissions({
        businessGrowthPartnerId: partnerId,
        page,
        size,
      });
      setCommissionsPage(result);
    } catch (error) {
      message.error(error?.message || "Failed to load partner commissions.");
    } finally {
      setCommissionsLoading(false);
    }
  }, [selectedPartner?.id]);

  useEffect(() => {
    loadPartners(0, partnersPaging.size);
  }, []);

  useEffect(() => {
    loadCommissions(selectedPartner?.id, 0, 100);
  }, [selectedPartner?.id]);

  const commissionSummary = useMemo(() => {
    const records = commissionsPage.content || [];
    const percentageRecords = records.filter((record) => record?.commissionValueType === "PERCENTAGE");
    const commissionTotal = records.reduce(
      (total, record) => total + Number(record?.commissionAmount || 0),
      0,
    );
    const activeRecords = records.filter((record) =>
      ["ACTIVE", "APPROVED", "PAID", "PENDING"].includes(String(record?.status || "").toUpperCase()),
    );

    return {
      commissionTotal,
      currency: records.find((record) => record?.currency)?.currency || "USD",
      percentageCount: percentageRecords.length,
      activeCount: activeRecords.length,
    };
  }, [commissionsPage.content]);

  const openPartnerPage = (partner = null) => {
    const isOrganization = partner?.partnerType === "ORGANIZATION";
    const reactivation = needsReactivation(partner);
    const selectedOrgId =
      isOrganization ? partnerOrganizationId(partner) : organizations[0]?.id || "";
    setEditingPartner(partner);
    partnerForm.setFieldsValue({
      submissionType: isOrganization ? SUBMISSION_TYPES.ORGANIZATION : SUBMISSION_TYPES.INDIVIDUAL,
      partnerType: isOrganization ? "ORGANIZATION" : "USER",
      displayName: partner?.displayName || userDisplayName(profileDetails || user),
      email: partner?.email || userEmail(profileDetails || user),
      userId: isOrganization ? "" : partner?.userId || actorId,
      organizationId: selectedOrgId,
      status: reactivation ? "PENDING" : partner?.status || "PENDING",
      confirmDetails: false,
    });
    setPartnerFormOpen(true);
  };

  const closePartnerPage = () => {
    setPartnerFormOpen(false);
    setEditingPartner(null);
    partnerForm.resetFields();
  };

  const savePartner = async () => {
    try {
      const values = await partnerForm.validateFields();
      const isOrganization = values.submissionType === SUBMISSION_TYPES.ORGANIZATION;
      const selectedOrganization =
        organizations.find((organization) => organization.id === values.organizationId) || null;
      const payload = {
        partnerType: isOrganization ? "ORGANIZATION" : "USER",
        userId: isOrganization ? undefined : actorId,
        organizationId: isOrganization ? values.organizationId : undefined,
        displayName: isOrganization
          ? organizationName(selectedOrganization)
          : userDisplayName(profileDetails || user),
        email: isOrganization
          ? organizationEmail(selectedOrganization) || userEmail(profileDetails || user)
          : userEmail(profileDetails || user),
        status: values.status,
      };
      setPartnerSaving(true);
      const saved = editingPartner?.id
        ? await partnersService.update({ actorId, id: editingPartner.id, payload })
        : await partnersService.create({ actorId, payload });
      message.success(
        needsReactivation(editingPartner)
          ? "BGP reactivation request submitted."
          : editingPartner?.id
            ? "BGP account updated."
            : "BGP account created.",
      );
      closePartnerPage();
      setSelectedPartner(saved);
      await loadPartners(partnersPaging.page, partnersPaging.size);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.message || "Failed to save partner.");
    } finally {
      setPartnerSaving(false);
    }
  };

  const cancelPartner = async () => {
    if (!selectedPartner?.id) return;

    try {
      setPartnerCancelling(true);
      const cancelled = await partnersService.cancel({
        actorId,
        id: selectedPartner.id,
      });
      message.success(
        isPendingPartner(selectedPartner)
          ? "BGP request cancelled."
          : "BGP account cancelled.",
      );
      setSelectedPartner(cancelled);
      await loadPartners(partnersPaging.page, partnersPaging.size);
    } catch (error) {
      message.error(error?.message || "Failed to cancel BGP account.");
    } finally {
      setPartnerCancelling(false);
    }
  };

  const partnerFormTitle = needsReactivation(editingPartner)
    ? "Request BGP Reactivation"
    : editingPartner
      ? "Edit BGP Account"
      : "Sign Up as Business Growth Partner";
  const partnerSubmitLabel = needsReactivation(editingPartner)
    ? "Request Reactivation"
    : editingPartner
      ? "Save"
      : "Sign Up";

  return (
    <div style={{ padding: 24, background: OLIVEEB_COLORS.bg, minHeight: "100%", fontSize: 14 }}>
      <div
        style={{
          border: `1px solid ${OLIVEEB_COLORS.border}`,
          borderRadius: 8,
          padding: 18,
          background: `linear-gradient(135deg, ${OLIVEEB_COLORS.white}, ${OLIVEEB_COLORS.greenSoft2})`,
          boxShadow: `0 14px 34px ${OLIVEEB_COLORS.shadow}`,
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col>
            <Space align="center" size={12}>
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: OLIVEEB_COLORS.oliveebBlueSoft,
                  color: OLIVEEB_COLORS.oliveebBlue,
                  fontSize: 24,
                }}
              >
                <FontAwesomeIcon icon="handshake" />
              </span>
              <div>
                <Title level={2} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Business Growth Partner - BGP
                </Title>
                <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                  Sign up and manage your Business Growth Partner account, referral code, and commissions.
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Button
                icon={<ReloadOutlined />}
                loading={partnersLoading}
                onClick={() => loadPartners(0, partnersPaging.size)}
                style={partnerButtonStyles.refresh}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={selectedPartner ? <EditOutlined /> : <PlusOutlined />}
                onClick={() => openPartnerPage(selectedPartner)}
                style={partnerButtonStyles.primary}
              >
                {needsReactivation(selectedPartner)
                  ? "Request Reactivation"
                  : selectedPartner
                    ? "Edit Account"
                    : "Sign Up as BGP"}
              </Button>
              <Button
                onClick={() => navigate(`${paths.partnerCommissions}?view=rates`)}
                style={{
                  ...partnerButtonBase,
                  background: OLIVEEB_COLORS.white,
                  borderColor: OLIVEEB_COLORS.oliveebOrange,
                  color: OLIVEEB_COLORS.oliveebOrange,
                }}
              >
                Commission Rates
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {partnerFormOpen ? (
        <Card
          size="small"
          bordered={false}
          title={<span style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>{partnerFormTitle}</span>}
          style={{
            borderRadius: 8,
            boxShadow: `0 12px 30px ${OLIVEEB_COLORS.shadow}`,
          }}
          bodyStyle={{ padding: 14 }}
        >
          <Form layout="vertical" form={partnerForm} initialValues={{ partnerType: "USER", status: "ACTIVE" }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 10, borderRadius: 8 }}
              message="Confirm your Business Growth Partner details"
              description={
                needsReactivation(editingPartner)
                  ? "Reactivation uses the same review process as a new BGP application. Confirm the details before submitting."
                : "These details identify who owns the referral code and who commissions are attributed to."
              }
            />
            <Form.Item
              name="submissionType"
              label="Submit as"
              rules={[{ required: true, message: "Select how you are submitting." }]}
              style={{ marginBottom: 10 }}
            >
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                options={[
                  { value: SUBMISSION_TYPES.INDIVIDUAL, label: "Individual" },
                  { value: SUBMISSION_TYPES.ORGANIZATION, label: "Organization" },
                ]}
              />
            </Form.Item>
            <Form.Item name="partnerType" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="userId" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="status" hidden>
              <Input />
            </Form.Item>

            {submissionType === SUBMISSION_TYPES.ORGANIZATION && (
              <Form.Item
                name="organizationId"
                label="Select Submitting Organization"
                rules={[{ required: true, message: "Select the organization submitting this BGP request." }]}
                style={{ marginBottom: 10 }}
              >
                <Select
                  size="small"
                  loading={partnersLoading}
                  placeholder="Select one of your organizations"
                  style={{
                    borderRadius: 8,
                    boxShadow: `0 8px 18px ${OLIVEEB_COLORS.oliveebBlue}18`,
                  }}
                  dropdownStyle={{
                    borderRadius: 8,
                    boxShadow: `0 14px 34px ${OLIVEEB_COLORS.shadow}`,
                  }}
                  options={organizations.map((organization) => ({
                    value: organization.id,
                    label: organizationName(organization),
                  }))}
                  notFoundContent="No organizations linked to your account"
                />
              </Form.Item>
            )}

            <Card
              size="small"
              title={
                <span style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                  {submissionType === SUBMISSION_TYPES.ORGANIZATION
                    ? "Organization Details"
                    : "Individual Details"}
                </span>
              }
              style={{
                borderRadius: 8,
                borderColor: OLIVEEB_COLORS.border,
                marginBottom: 10,
              }}
              bodyStyle={{ padding: "8px 12px" }}
            >
              {submissionType === SUBMISSION_TYPES.ORGANIZATION ? (
                selectedSubmissionOrganization ? (
                  <>
                    <InfoLine label="Organization Name" value={organizationName(selectedSubmissionOrganization)} />
                    <InfoLine
                      label="Organization ID"
                      value={selectedSubmissionOrganization.id}
                      copyValue={selectedSubmissionOrganization.id}
                      copyLabel="Organization ID"
                      code
                    />
                    <InfoLine
                      label="Email"
                      value={organizationEmail(selectedSubmissionOrganization)}
                      warning={!organizationEmail(selectedSubmissionOrganization)}
                    />
                    <InfoLine
                      label="Phone"
                      value={organizationPhone(selectedSubmissionOrganization)}
                      warning={!organizationPhone(selectedSubmissionOrganization)}
                    />
                    <InfoLine
                      label="Address"
                      value={formatAddress(organizationAddress(selectedSubmissionOrganization))}
                      warning={!organizationAddress(selectedSubmissionOrganization)}
                    />
                    <InfoLine
                      label="Tax Number / EIN"
                      value={maskedSensitive(
                        selectedSubmissionOrganization.ein ||
                        selectedSubmissionOrganization.taxId ||
                        selectedSubmissionOrganization.taxNumber ||
                        selectedSubmissionOrganization.registrationNumber,
                      )}
                      warning={
                        !(
                          selectedSubmissionOrganization.ein ||
                          selectedSubmissionOrganization.taxId ||
                          selectedSubmissionOrganization.taxNumber ||
                          selectedSubmissionOrganization.registrationNumber
                        )
                      }
                    />
                  </>
                ) : (
                  <Empty description="Select an organization to review its details." />
                )
              ) : (
                <>
                  <InfoLine label="First Name" value={profileValue(profileDetails || user, "firstName")} warning={!profileValue(profileDetails || user, "firstName")} />
                  <InfoLine label="Middle Name" value={profileValue(profileDetails || user, "middleName")} />
                  <InfoLine label="Last Name" value={profileValue(profileDetails || user, "lastName")} warning={!profileValue(profileDetails || user, "lastName")} />
                  <InfoLine label="Email" value={userEmail(profileDetails || user)} warning={!userEmail(profileDetails || user)} />
                  <InfoLine label="Phone" value={profileValue(profileDetails || user, "phone", "contactDetails.mobilePhone")} warning={!profileValue(profileDetails || user, "phone", "contactDetails.mobilePhone")} />
                  <InfoLine label="Gender" value={profileValue(profileDetails || user, "gender")} warning={!profileValue(profileDetails || user, "gender")} />
                  <InfoLine label="Date of Birth" value={profileValue(profileDetails || user, "birthDate", "dateOfBirth")} warning={!profileValue(profileDetails || user, "birthDate", "dateOfBirth")} />
                  <InfoLine label="SSN" value={maskedSensitive(profileValue(profileDetails || user, "ssn"))} warning={!profileValue(profileDetails || user, "ssn")} />
                  <InfoLine
                    label="Identification"
                    value={profileValue(profileDetails || user, "identificationDocumentType", "identityVerificationStatus")}
                    warning={!profileValue(profileDetails || user, "identificationDocumentType", "identityVerificationStatus")}
                  />
                  <InfoLine
                    label="Address"
                    value={
                      formatAddress(profileValue(profileDetails || user, "residentialAddress")) ||
                      formatAddress(profileValue(profileDetails || user, "permanentAddress")) ||
                      formatAddress(profileValue(profileDetails || user, "billingAddress"))
                    }
                    warning={
                      !(
                        profileValue(profileDetails || user, "residentialAddress") ||
                        profileValue(profileDetails || user, "permanentAddress") ||
                        profileValue(profileDetails || user, "billingAddress")
                      )
                    }
                  />
                </>
              )}
            </Card>

            <Space wrap size={8} style={{ marginBottom: 10 }}>
              <Button size="small" onClick={() => navigate(paths.profile)} style={partnerButtonStyles.profile}>
                Update Personal Details
              </Button>
              {submissionType === SUBMISSION_TYPES.ORGANIZATION && (
                <Button size="small" onClick={() => navigate(paths.workspaces)} style={partnerButtonStyles.organization}>
                  Update Organization Details
                </Button>
              )}
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => loadPartners(0, partnersPaging.size)}
                style={partnerButtonStyles.refresh}
              >
                Refresh Details
              </Button>
            </Space>

            <Form.Item
              name="confirmDetails"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error("Confirm the details before submitting.")),
                },
              ]}
            >
              <Checkbox style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 14 }}>
                I have reviewed these details and confirm they are correct for this BGP request.
              </Checkbox>
            </Form.Item>
            <Space size={8}>
              <Button size="small" onClick={closePartnerPage} style={partnerButtonStyles.back}>
                Back
              </Button>
              <Button
                size="small"
                loading={partnerSaving}
                onClick={savePartner}
                style={partnerButtonStyles.submit}
              >
                Submit
              </Button>
            </Space>
          </Form>
        </Card>
      ) : (
        <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              border: isCancelledPartner(selectedPartner)
                ? `1px solid ${OLIVEEB_COLORS.oliveebRed}`
                : undefined,
              background: isCancelledPartner(selectedPartner)
                ? OLIVEEB_COLORS.redSoft
                : undefined,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <Statistic
                title="BGP Status"
                value={selectedPartner?.status || "Not signed up"}
                prefix={isCancelledPartner(selectedPartner) ? <StopOutlined /> : null}
                valueStyle={{
                  color: isCancelledPartner(selectedPartner)
                    ? OLIVEEB_COLORS.oliveebRed
                    : selectedPartner
                      ? OLIVEEB_COLORS.oliveebGreen
                      : OLIVEEB_COLORS.oliveebOrange,
                }}
              />
              {canCancelPartner(selectedPartner) ? (
                <Popconfirm
                  title={isPendingPartner(selectedPartner) ? "Cancel BGP request?" : "Cancel BGP account?"}
                  description={
                    isPendingPartner(selectedPartner)
                      ? "This pending BGP request will be cancelled."
                      : "Your referral code will stop earning new commissions."
                  }
                  okText={isPendingPartner(selectedPartner) ? "Cancel Request" : "Cancel BGP"}
                  cancelText="Keep Active"
                  okButtonProps={{ danger: true, loading: partnerCancelling }}
                  onConfirm={cancelPartner}
                >
                  <Button
                    danger
                    icon={<StopOutlined />}
                    loading={partnerCancelling}
                    style={partnerButtonStyles.cancel}
                  >
                    {isPendingPartner(selectedPartner) ? "Cancel Request" : "Cancel BGP"}
                  </Button>
                </Popconfirm>
              ) : null}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <div style={{ textAlign: "center" }}>
              <Text type="secondary">BGP Code (Referral Code)</Text>
            </div>
            <div style={{ marginTop: 8, textAlign: "center" }}>
              <Space size={8} align="center">
                <Text
                  strong
                  style={{
                    color: OLIVEEB_COLORS.textPrimary,
                    fontSize: 26,
                    lineHeight: 1.2,
                    wordBreak: "break-all",
                  }}
                >
                  {selectedPartner?.referralCode || "-"}
                </Text>
                {selectedPartner?.referralCode ? (
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyText(selectedPartner.referralCode, "BGP Code (Referral Code)")}
                    style={partnerButtonStyles.refresh}
                  />
                ) : null}
              </Space>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic title="Commission rules" value={commissionsPage.totalElements || 0} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <Card
            title={<span style={{ color: OLIVEEB_COLORS.oliveebGreen }}>My BGP Account</span>}
            bordered={false}
            style={{ borderRadius: 8 }}
          >
            {!selectedPartner ? (
              <Empty
                description={
                  <span>
                    You have not signed up as a Business Growth Partner yet.
                  </span>
                }
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={partnersLoading}
                  onClick={() => openPartnerPage()}
                  style={partnerButtonStyles.primary}
                >
                  Sign Up as BGP
                </Button>
              </Empty>
            ) : (
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <Space direction="vertical" size={2}>
                  <Text strong style={{ fontSize: 22, color: OLIVEEB_COLORS.textPrimary }}>
                    {partnerName(selectedPartner)}
                  </Text>
                  <Text type="secondary">{selectedPartner.email || "-"}</Text>
                </Space>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Tag color="cyan">{selectedPartner.partnerType}</Tag>
                  </Col>
                  <Col span={12}>
                    <Tag color={statusColor(selectedPartner.status)}>{selectedPartner.status}</Tag>
                  </Col>
                </Row>
                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                  <Text type="secondary">Partner ID</Text>
                  <Space>
                    <Text code style={{ whiteSpace: "normal", wordBreak: "break-all" }}>
                      {selectedPartner.id || "-"}
                    </Text>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyText(selectedPartner.id, "Partner ID")}
                      style={partnerButtonStyles.back}
                    />
                  </Space>
                  <div>
                    <div>
                      <Text type="secondary">BGP Code (Referral Code)</Text>
                    </div>
                    <Space style={{ marginTop: 3 }}>
                      <Text code style={{ fontSize: 17 }}>
                        {selectedPartner.referralCode || "-"}
                      </Text>
                      {selectedPartner.referralCode && (
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyText(selectedPartner.referralCode, "BGP Code (Referral Code)")}
                          style={partnerButtonStyles.refresh}
                        />
                      )}
                    </Space>
                  </div>
                  <Text type="secondary">Signed Up</Text>
                  <Text>{formatDateTime(selectedPartner.createdAt)}</Text>
                </Space>
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} xl={14}>
          <Card
            title={<span style={{ color: OLIVEEB_COLORS.oliveebGreen }}>My BGP Commissions</span>}
            extra={
              <Button
                onClick={() => navigate(paths.partnerCommissions)}
                loading={commissionsLoading}
                style={partnerButtonStyles.back}
              >
                View Commissions
              </Button>
            }
            bordered={false}
            style={{ borderRadius: 8 }}
          >
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Statistic
                  title="Commission Records"
                  value={commissionsPage.totalElements || 0}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebBlue }}
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Commission Total"
                  value={money(commissionSummary.commissionTotal, commissionSummary.currency)}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Percentage Rules"
                  value={commissionSummary.percentageCount}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebOrange }}
                />
              </Col>
            </Row>
            <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
              Automatic BGP commissions from subscriptions using your referral code.
            </Text>
          </Card>
        </Col>
      </Row>
      </>
      )}
    </div>
  );
};

export default Partners;
